import { useState, useCallback, useEffect, type MutableRefObject } from "react";
import { Box, Button, Flex } from "@mantine/core";
import { IconBookmark } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import WorkspacePartnerCompanyHeader from "@/pages/collaborations/components/WorkspacePartnerCompanyHeader";
import {
  useWorkspacePartnerHeader,
  WORKSPACE_PARTNER_BOOKMARK_BTN_STYLES,
} from "@/pages/collaborations/components/useWorkspacePartnerHeader";
import ExpressInterestModal, { type ExpressInterestFormData } from "@/components/shared/ExpressInterestModal";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";
import CollaborationPaymentSuccessModal from "@/pages/interests/components/CollaborationPaymentSuccessModal";
import PaymentWindowExtendModal from "@/components/shared/PaymentWindowExtendModal";
import InfoBanner from "@/components/shared/InfoBanner";
import { IconExclamationCircle } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { useUser } from "@/context/UserContext";
import { CollaborationStatus, STATUS_COLORS } from "@/components/shared/constants/general.const";
import { getCollaborationStatusConfig, isCollaborationPaymentPhase } from "@/shared/utils/collaboration-helpers";
import { useCollaborationEngagementPayment } from "@/pages/interests/hooks/useCollaborationEngagementPayment";
import {
  formatPaymentHoursLabel,
  isPaymentUiWarningUrgent,
  usePaymentWindowUiDemo,
} from "@/shared/utils/paymentWindowUiDemo";

export type WorkspacePartnerSectionProps = {
  company: any;
  collaboration: any | null;
  onCollaborationChange: (collaboration: any | null) => void;
  /** After first express interest, parent may navigate to `/collaborations/:matchId`. */
  onMatchCreated?: (matchId: string) => void;
  showShortlist?: boolean;
  /** When false, parent owns payment modals (e.g. match workspace page banner). */
  showPaymentModals?: boolean;
  onRequestPayment?: () => void;
  /** Parent can trigger Express Interest modal (e.g. locked Assessment/Documents tabs). */
  expressInterestOpenerRef?: MutableRefObject<(() => void) | null>;
  /** Hide partner header card (e.g. pre–express-interest preview uses a banner only). */
  hideCompanyHeaderCard?: boolean;
  /** Hide "Ready to collaborate" payment banner (parent shows PreviewPaymentBanner instead). */
  hidePaymentBanner?: boolean;
};

export default function WorkspacePartnerSection({
  company,
  collaboration,
  onCollaborationChange,
  onMatchCreated,
  showShortlist = true,
  showPaymentModals = true,
  onRequestPayment,
  expressInterestOpenerRef,
  hideCompanyHeaderCard = false,
  hidePaymentBanner = false,
}: WorkspacePartnerSectionProps) {
  const {
    userDetails,
    addInterestedCompany,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  } = useUser();

  const [expressInterestModalOpened, { open: openExpressInterestModal, close: closeExpressInterestModal }] =
    useDisclosure(false);

  useEffect(() => {
    if (!expressInterestOpenerRef) return;
    expressInterestOpenerRef.current = openExpressInterestModal;
    return () => {
      expressInterestOpenerRef.current = null;
    };
  }, [expressInterestOpenerRef, openExpressInterestModal]);
  const [requestLoading, setRequestLoading] = useState(false);

  const companyId = company?.id as string | undefined;
  const userCompanyId = userDetails?.company?.id;

  const { isFavorited, handleShortlistClick } = useWorkspacePartnerHeader({
    company,
    collaboration,
    onCollaborationChange,
  });

  const refetchCollaboration = useCallback(async () => {
    if (!companyId) return;
    try {
      const next = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyId });
      onCollaborationChange(next);
    } catch {
      onCollaborationChange(null);
    }
    refreshPendingInterestCount();
    refreshCollaborationSidebarCounts();
  }, [companyId, onCollaborationChange, refreshPendingInterestCount, refreshCollaborationSidebarCounts]);

  const { engagementFeeCredits, paymentModalOpened, paymentLoading, beginEngagementPayment, handlePaymentModalClose, handlePaymentConfirm, successData, handleSuccessModalClose } =
    useCollaborationEngagementPayment(refetchCollaboration);

  const isPaymentPhase =
    Boolean(collaboration?.paymentDeadline) &&
    (collaboration?.status === CollaborationStatus.BOTH_ACCEPTED ||
      collaboration?.status === CollaborationStatus.REQUESTER_PAID_ONLY ||
      collaboration?.status === CollaborationStatus.RECEIVER_PAID_ONLY);

  const { displayMs, modalOpen, handleExtend, handleModalDismiss } = usePaymentWindowUiDemo(
    collaboration?.id,
    collaboration?.paymentDeadline,
    isPaymentPhase
  );

  const handleConfirmExpressInterest = async (data: ExpressInterestFormData) => {
    if (!companyId) return;
    setRequestLoading(true);
    try {
      const created = await trpc.collaboration.request.mutate({
        targetCompanyId: companyId,
        requesterRole: data.requesterRole,
        expectedPartnerRole: data.expectedPartnerRole,
        expressInterestTitle: data.expressInterestTitle ?? undefined,
        expressInterestSummary: data.expressInterestSummary ?? undefined,
      });
      addInterestedCompany(companyId);
      refreshPendingInterestCount();
      successNotification({
        title: "Success",
        message: "Your interest has been expressed to the company",
      });
      closeExpressInterestModal();
      onCollaborationChange(created);
      onMatchCreated?.(created.id);
    } catch (error) {
      handleTrpcError(error);
      closeExpressInterestModal();
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMakePayment = () => {
    if (onRequestPayment) {
      onRequestPayment();
      return;
    }
    if (collaboration?.id) beginEngagementPayment(collaboration.id);
  };

  const isRequester = collaboration?.requesterCompanyId === userCompanyId;
  const userPaid = collaboration
    ? isRequester
      ? Boolean(collaboration.requesterPaid)
      : Boolean(collaboration.receiverPaid)
    : false;

  const showPaymentBanner = Boolean(collaboration && isCollaborationPaymentPhase(collaboration) && !userPaid);

  const showPendingAcceptanceBanner =
    collaboration?.status === CollaborationStatus.REQUESTED && isRequester;

  const requestSentChipConfig = getCollaborationStatusConfig(CollaborationStatus.REQUESTED);

  const pendingAcceptanceBannerActions = showPendingAcceptanceBanner ? (
    <Flex align="center" gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
      <Box
        px={16}
        py={7}
        style={{
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: requestSentChipConfig.bg,
          color: requestSentChipConfig.text,
          fontSize: "13px",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {requestSentChipConfig.label}
      </Box>
      {showShortlist ? (
        <Button
          variant="filled"
          size="md"
          p="xs"
          onClick={handleShortlistClick}
          styles={WORKSPACE_PARTNER_BOOKMARK_BTN_STYLES}
          aria-label={isFavorited ? "Remove from shortlist" : "Add to shortlist"}
        >
          <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
        </Button>
      ) : null}
    </Flex>
  ) : null;

  return (
    <Box mb={hideCompanyHeaderCard && !collaboration ? 0 : "md"}>
      {!hideCompanyHeaderCard && !showPendingAcceptanceBanner ? (
        <WorkspacePartnerCompanyHeader
          company={company}
          collaboration={collaboration}
          onCollaborationChange={onCollaborationChange}
          showShortlist={showShortlist}
          onExpressInterestClick={openExpressInterestModal}
        />
      ) : null}

      {collaboration?.paymentDeadline && displayMs != null && displayMs > 0 && isPaymentUiWarningUrgent(displayMs) ? (
        <Box mb="md">
          <InfoBanner
            icon={IconExclamationCircle}
            iconColor={STATUS_COLORS.WARNING.text}
            title={`Payment window expires in ${formatPaymentHoursLabel(displayMs)}`}
            subtitle="Both parties must complete payment within 96 hours of the first payment."
            bgColor={STATUS_COLORS.WARNING.bg}
            borderColor={STATUS_COLORS.WARNING.border}
            titleColor={STATUS_COLORS.WARNING.text}
            subtitleColor={STATUS_COLORS.WARNING.text}
          />
        </Box>
      ) : null}

      {showPendingAcceptanceBanner ? (
        <Box mb="md">
          <InfoBanner
            title="Waiting for acceptance"
            subtitle="Your interest request was sent. The other company must accept before you can pay and unlock the full collaboration workspace."
            bgColor={STATUS_COLORS.WARNING.bg}
            borderColor={STATUS_COLORS.WARNING.border}
            titleColor={STATUS_COLORS.WARNING.text}
            subtitleColor={STATUS_COLORS.WARNING.text}
            rightSection={pendingAcceptanceBannerActions}
          />
        </Box>
      ) : null}

      {showPaymentBanner && !hidePaymentBanner ? (
        <Box mb="md">
          <InfoBanner
            title="Ready to collaborate"
            subtitle="Complete payment to unlock full profile details, chat, and document sharing."
            bgColor={STATUS_COLORS.WARNING.bg}
            borderColor={STATUS_COLORS.WARNING.border}
            titleColor={STATUS_COLORS.WARNING.text}
            subtitleColor={STATUS_COLORS.WARNING.text}
            actionLabel="Make payment"
            onAction={handleMakePayment}
          />
        </Box>
      ) : null}

      <ExpressInterestModal
        opened={expressInterestModalOpened}
        onClose={closeExpressInterestModal}
        onConfirm={handleConfirmExpressInterest}
        loading={requestLoading}
        targetCompanyId={companyId ?? ""}
        userWhoAmI={(userDetails?.company?.whoAmI as string[]) || []}
        targetCollaborationIntent={(company?.collaborationIntent as string[]) || []}
      />

      {showPaymentModals ? (
        <>
          <PaymentMethodModal
            opened={paymentModalOpened}
            onClose={handlePaymentModalClose}
            onConfirm={handlePaymentConfirm}
            loading={paymentLoading}
            creditsRequired={engagementFeeCredits}
          />
          <CollaborationPaymentSuccessModal
            opened={!!successData}
            onClose={handleSuccessModalClose}
            paymentData={successData}
          />
        </>
      ) : null}

      <PaymentWindowExtendModal opened={modalOpen} onExtend={handleExtend} onDismiss={handleModalDismiss} />
    </Box>
  );
}
